import DatePicker from "../../../DomUtilities/DatePicker";
import Loader from "../../../DomUtilities/Loader";
import openErrorModal from "../../../Error/ErrorModal";
import { openBackendValidationErrorModal } from "../../../Error/ErrorUtilities";
import { GetOrder, fetchOrderUpdateModal, updateOrder, updateOrderCustomer } from "../../../HTTPRequests/OrderRequests";
import Order from "../../../Models/Order";
import Mediator from "../../../Utilities/Mediator";
import UpdateItemRow from "./UpdateItemRow";
import UpdateOrder from "../../../Models/UpdateOrder/UpdateOrder";
import UpdateCustomer from "./UpdateCustomer";
import OrderOverviewModalsHandler from "../OrderOverviewModalsHandler";

export default class OrderUpdateModal {
	modalDiv: HTMLDivElement;
	order: Order;
	updatedOrder: UpdateOrder;
	loaded: number;
	modalHtmlString: string;
	readonly NUMBER_OF_TASKS: number = 2;
	saveButton: HTMLButtonElement;
	isUpdateCustomer: boolean;
	orderUpdateModalsHandler: OrderOverviewModalsHandler;

	constructor() {
		this.modalDiv = document.createElement("div");
		this.loaded = 0;
		this.isUpdateCustomer = false;
		this.updatedOrder = new UpdateOrder();
	}

	public init(editIcon: HTMLImageElement) {
		editIcon.onclick = (event) => {
			event.stopPropagation();
			this.initUpdateModal(editIcon.dataset.id);
		}
	}

	public initUpdateModal(id: string) {
		this.updatedOrder.id = id;
		this.fetchOrderEntity(id);
		this.fetchUpdateModal(id);
	}

	private fetchUpdateModal(id: string) {
		Loader.show();
		fetchOrderUpdateModal(id)
			.then((response: string) => {
				this.modalHtmlString = response;
				this.loaded++;
				this.openUpdateModal();
			}, () => { //backend error
				openErrorModal();
			}).catch(() => { //frontend error
				openErrorModal();
			}).finally(() => {
				Loader.hide();
			});
	}

	private fetchOrderEntity(id: string) {
		Loader.show();
		GetOrder(id)
			.then((response: Order) => {
				this.order = response;
				this.loaded++;
				this.openUpdateModal();
			}, () => { //backend error
				openErrorModal();
			}).catch(() => { //frontend error
				openErrorModal();
			}).finally(() => {
				Loader.hide();
			});
	}

	private fetchOrderItemList() {
		if (!this.isUpdateCustomer) {
			const itemsList: HTMLUListElement = this.modalDiv.querySelector(".update-items > ul:first-of-type");
			this.order.items.forEach((item) => {
				const updateItem = new UpdateItemRow(item, this.updatedOrder);
				itemsList.appendChild(updateItem.listItem);
				itemsList.appendChild(updateItem.serviceList);
			});
		}
	}

	private openUpdateModal() {
		if (this.loaded === this.NUMBER_OF_TASKS) {
			this.modalDiv.innerHTML = this.modalHtmlString;
			document.body.appendChild(this.modalDiv);
			Mediator.emit("scanEnabled", false);

			this.addModalClickListeners();
			this.initDatePicker();
			this.fetchOrderItemList();
			new UpdateCustomer(this.order, this.updatedOrder).init(this.modalDiv, this.saveButton);
			if (this.isUpdateCustomer) {
				this.orderUpdateModalsHandler = new OrderOverviewModalsHandler(this.order.id, this.modalDiv);
			}
		}
	}

	private addModalClickListeners() {
		const closeImg: HTMLImageElement = this.modalDiv.querySelector('img[alt="close icon"]');
		closeImg.onclick = () => {
			this.closeModal();
		}

		this.modalDiv.onclick = (event) => {
			if (event.target === this.modalDiv.firstElementChild) {
				this.closeModal();
			}
		}

		this.saveButton = this.modalDiv.querySelector('.print-btn');
		this.saveButton.onclick = () => {
			if (this.isOrderUpdated) {
				this.saveUpdatedOrder();
			} else {
				this.closeModal();
			}
		}
	}

	private closeModal() {
		this.loaded = 0;
		Mediator.emit("scanEnabled", true);
		this.modalDiv.remove();
	}

	private get isOrderUpdated(): boolean {
		return !!this.updatedOrder.deliveryDate
			|| !!this.updatedOrder.customerName
			|| !!this.updatedOrder.customerPhoneNumber
			|| !!this.updatedOrder.customerEmail
			|| this.updatedOrder.items.length > 0;
	}

	private saveUpdatedOrder() {
		if (this.isUpdateCustomer) {
			this.updateCustomerInfo();
		} else {
			this.updateOrder();
		}
	}

	private updateCustomerInfo() {
		Loader.show();

		updateOrderCustomer(this.updatedOrder)
			.then(() => {
				this.closeModal();
				Mediator.emit("orderOverviewOperationFinished");
			}, (error) => { //backend error
				openBackendValidationErrorModal(error);
			}).catch(() => { //frontend error
				openErrorModal();
			}).finally(() => {
				Loader.hide();
			});
	}

	private updateOrder() {
		Loader.show();

		updateOrder(this.updatedOrder)
			.then(() => {
				this.closeModal();
				Mediator.emit("orderOverviewOperationFinished");
			}, (error) => { //backend error
				openBackendValidationErrorModal(error);
			}).catch(() => { //frontend error
				openErrorModal();
			}).finally(() => {
				Loader.hide();
			});
	}

	private initDatePicker() {
		const dateInput = this.modalDiv.querySelector<HTMLInputElement>('#date');
		this.isUpdateCustomer = !dateInput;

		if (!this.isUpdateCustomer) {
			const language: string = dateInput.dataset.lang;
			const datePicker = new DatePicker("#date", language);
			datePicker.setProperty("disable",
				[
					date => date.getDay() === 0 || date.getDay() === 6
				]);
			datePicker.setProperty("minDate", new Date());
			datePicker.setProperty("closeOnSelect", true);
			this.order.deliveryDate = datePicker.DateValue;
			this.addDatePickerOnChangeListener(dateInput, datePicker);
		}
	}

	private addDatePickerOnChangeListener(dateInput: HTMLInputElement, datePicker: DatePicker) {
		dateInput.onchange = () => {
			if (datePicker.isValid && datePicker.DateValue.toDateString() !== this.order.deliveryDate.toDateString()) {
				this.updatedOrder.deliveryDate = datePicker.DateValue;
			} else {
				this.updatedOrder.deliveryDate = null;
			}
		}
	}
}
